import { useState } from "react";
import { Link } from "react-router-dom";
import { Flame, Phone, Menu, X, ChevronDown } from "lucide-react";
import { useScrollHeader } from "@/hooks/useScrollHeader";
import { useLocationContext } from "@/hooks/useLocationContext";
import { QuoteTriggerButton } from "@/components/quote/QuoteTriggerButton";
import { CallRailPhone } from "@/components/shared/CallRailPhone";
import { bbqNavLinks } from "@/lib/navLinks";
import { trackNavigation } from "@/lib/analytics";
import bbqLogo64 from "@/assets/bbq-logo-64.webp";
import bbqLogo128 from "@/assets/bbq-logo-128.webp";

// Location-specific header content
const getLocationHeaderContent = (region: 'texas' | 'texas-houston' | 'texas-austin' | 'texas-san-antonio' | 'california' | 'national') => {
  switch (region) {
    case 'california':
      return {
        announcement: "SOUTHERN CALIFORNIA'S OUTDOOR KITCHEN SPECIALISTS — FACTORY DIRECT PRICES",
        subtext: "Serving the Inland Empire, Los Angeles, Orange County & All of SoCal",
        hours: "Mon-Fri 10-5 | Sat 10-5 | Sun 11-4",
      };
    case 'texas-houston':
      return {
        announcement: "HOUSTON'S OUTDOOR KITCHEN SPECIALISTS — FACTORY DIRECT PRICES",
        subtext: "Serving Greater Houston, Katy, Sugar Land & Surrounding Areas",
        hours: "Factory-Direct Delivery Available",
      };
    case 'texas-austin':
      return {
        announcement: "AUSTIN'S OUTDOOR KITCHEN SPECIALISTS — FACTORY DIRECT PRICES",
        subtext: "Serving Greater Austin, Round Rock, Cedar Park & Surrounding Areas",
        hours: "Factory-Direct Delivery Available",
      };
    case 'texas-san-antonio':
      return {
        announcement: "SAN ANTONIO'S OUTDOOR KITCHEN SPECIALISTS — FACTORY DIRECT PRICES",
        subtext: "Serving Greater San Antonio, New Braunfels, Boerne & Surrounding Areas",
        hours: "Factory-Direct Delivery Available",
      };
    case 'texas':
      return {
        announcement: "DFW'S #1 OUTDOOR KITCHEN SPECIALISTS — FACTORY DIRECT PRICES",
        subtext: "Serving Dallas, Fort Worth, Plano, Frisco & All DFW",
        hours: "Mon-Sat 10-6 | Sun 12-5",
      };
    case 'national':
    default:
      return {
        announcement: "AMERICA'S OUTDOOR KITCHEN SPECIALISTS — FACTORY DIRECT PRICES",
        subtext: "Showrooms in Texas & California",
        hours: "Mon-Sat 10-6 | Sun 12-5",
      };
  }
};

export const BBQNav = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const isScrolled = useScrollHeader();
  const { region } = useLocationContext();
  
  const headerContent = getLocationHeaderContent(region);

  return (
    <>
      {/* Announcement Bar */}
      <div className="bg-gradient-to-r from-orange-600 via-orange-500 to-amber-500 text-white py-2 text-center">
        <p className="text-sm font-bold tracking-wide flex items-center justify-center gap-2">
          <Flame className="w-4 h-4" />
          {headerContent.announcement}
          <Flame className="w-4 h-4" />
        </p>
      </div>

      {/* Main Header */}
      <header className={`bg-[#2a2520] border-b-4 border-orange-500 sticky top-0 z-50 transition-all duration-300 ${isScrolled ? 'shadow-lg bg-[#2a2520]/95 backdrop-blur-md' : ''}`}>
        <div className="container mx-auto px-4">
          {/* Top Bar */}
          <div className="flex items-center justify-between py-2 border-b border-[#3a3530] text-sm">
            <span className="text-[#f5e6d3]/70 hidden md:block">
              {headerContent.subtext}
            </span>
            <div className="flex items-center gap-6">
              <div className="text-orange-400 hover:text-orange-300 font-bold flex items-center gap-2 transition-colors duration-200">
                <Phone className="w-4 h-4" />
                <CallRailPhone 
                  source={`bbq_nav_header_${region}`}
                  className="text-orange-400 hover:text-orange-300 callrail-phone"
                />
              </div>
              <span className="text-[#f5e6d3]/50 hidden md:block">{headerContent.hours}</span>
            </div>
          </div>

          {/* Main Nav */}
          <div className="flex items-center justify-between py-3">
            {/* Logo - optimized sizes for responsive loading */}
            <Link to="/" className="flex-shrink-0">
              <img 
                src={bbqLogo128}
                srcSet={`${bbqLogo64} 64w, ${bbqLogo128} 128w`}
                sizes="(max-width: 768px) 56px, 64px"
                alt="BBQ Island Liquidators" 
                className="h-14 md:h-16 w-auto"
                width="64"
                height="64"
                loading="eager"
                decoding="sync"
              />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden xl:flex items-center gap-1">
              {bbqNavLinks.map((link) => (
                <div key={link.label} className="relative group">
                  <Link
                    to={link.href}
                    onClick={() => trackNavigation('bbq', link.label.toLowerCase())}
                    className={`flex items-center gap-1 px-3 py-2 text-sm font-semibold uppercase tracking-wider transition-colors duration-200 ${
                      link.highlight 
                        ? "text-orange-400 hover:text-orange-300" 
                        : "text-[#f5e6d3] hover:text-orange-400"
                    }`}
                  >
                    {link.label}
                    {link.submenu && <ChevronDown className="w-3 h-3 transition-transform duration-200 group-hover:rotate-180" />}
                  </Link>
                  {link.submenu && (
                    <div className="absolute top-full left-0 bg-[#1a1a1a] border border-[#3a3530] min-w-[220px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 dropdown-content">
                      {link.submenu.map((sub) => (
                        <Link
                          key={sub.href}
                          to={sub.href}
                          onClick={() => trackNavigation('bbq', `${link.label}_${sub.label}`.toLowerCase())}
                          className="block px-4 py-3 text-[#f5e6d3] hover:bg-orange-500 hover:text-white text-sm transition-colors duration-150"
                        >
                          {sub.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* CTA */}
            <div className="hidden md:flex items-center gap-3">
              <QuoteTriggerButton className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-6">
                GET QUOTE
              </QuoteTriggerButton>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="xl:hidden text-[#f5e6d3] p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <nav className="xl:hidden bg-[#1a1a1a] border-t border-[#3a3530] max-h-[80vh] overflow-y-auto">
            <div className="container mx-auto px-4 py-4 space-y-1">
              {bbqNavLinks.map((link) => (
                <div key={link.label}>
                  <div className="flex items-center justify-between">
                    <Link
                      to={link.href}
                      className={`block py-3 font-semibold uppercase tracking-wider ${
                        link.highlight ? "text-orange-400" : "text-[#f5e6d3]"
                      }`}
                      onClick={() => !link.submenu && setMobileMenuOpen(false)}
                    >
                      {link.label}
                    </Link>
                    {link.submenu && (
                      <button
                        onClick={() => setOpenSubmenu(openSubmenu === link.label ? null : link.label)}
                        className="p-2 text-[#f5e6d3]"
                      >
                        <ChevronDown className={`w-4 h-4 transition-transform ${openSubmenu === link.label ? "rotate-180" : ""}`} />
                      </button>
                    )}
                  </div>
                  {link.submenu && openSubmenu === link.label && (
                    <div className="pl-4 pb-2 space-y-1">
                      {link.submenu.map((sub) => (
                        <Link
                          key={sub.href}
                          to={sub.href}
                          className="block py-2 text-[#f5e6d3]/70 hover:text-orange-400"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {sub.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="pt-4 border-t border-[#3a3530] space-y-3">
                <QuoteTriggerButton 
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  GET A FREE QUOTE
                </QuoteTriggerButton>
              </div>
            </div>
          </nav>
        )}
      </header>
    </>
  );
};
